import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, TrendingUp, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

const Reports = () => {
  const [reportType, setReportType] = useState('daily');
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const generateReport = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/generate-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ report_type: reportType })
      });

      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Report generation error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/download-report?type=${reportType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ShansoZen_${reportType}_report.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-8 h-8 text-green-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">AI Insights & Reports</h2>
          <p className="text-gray-400">Generate comprehensive health reports</p>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Select Report Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { value: 'daily', label: 'Daily Summary', icon: Calendar, description: 'Today\'s health overview' },
            { value: 'weekly', label: 'Weekly Report', icon: TrendingUp, description: 'Past 7 days analysis' },
            { value: 'monthly', label: 'Monthly Report', icon: FileText, description: 'Full month insights' }
          ].map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setReportType(type.value)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  reportType === type.value
                    ? 'bg-green-500/20 border-green-500 text-white'
                    : 'bg-gray-700/50 border-gray-600 text-gray-400 hover:border-green-500/50'
                }`}
              >
                <Icon className={`w-6 h-6 mx-auto mb-2 ${
                  reportType === type.value ? 'text-green-400' : 'text-gray-400'
                }`} />
                <p className="font-semibold mb-1">{type.label}</p>
                <p className="text-xs text-gray-400">{type.description}</p>
              </button>
            );
          })}
        </div>

        <Button
          onClick={generateReport}
          disabled={isGenerating}
          className="w-full mt-6 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 py-4 text-lg"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Generating Report...
            </>
          ) : (
            <>
              <TrendingUp className="w-5 h-5 mr-2" />
              Generate {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
            </>
          )}
        </Button>

        {error && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Generated Report */}
      {report && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">{report.title}</h3>
            <Button
              onClick={downloadPDF}
              className="bg-green-500 hover:bg-green-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>

          {/* Report Summary */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-green-400 mb-2">Summary</h4>
            <p className="text-gray-300 whitespace-pre-line">{report.summary}</p>
          </div>

          {/* Key Metrics */}
          {report.metrics && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">Key Metrics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(report.metrics).map(([key, value]) => (
                  <div key={key} className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-400 mb-1">{key.replace('_', ' ').toUpperCase()}</p>
                    <p className="text-2xl font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Recommendations */}
          {report.recommendations && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">AI Recommendations</h4>
              <div className="space-y-2">
                {report.recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 flex items-start gap-2">
                    <span className="text-purple-400 font-bold">•</span>
                    <p className="text-gray-300">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
